import { Button, Checkbox, Modal } from 'antd';
import { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { SELECT_DATE } from 'common/constants/constants';
import AttributePicker from '../../../../../../../AttributePicker';
import AttributeFilterAndComparatorSelector from './components/AttributeFilterAndComparatorSelector';
import DatePickerModal from './components/DatePickerModal';
import { useModalFilters } from './utils/useModalFilters';
import { useDatePickerFlow } from './utils/useDatePickerFlow';
import { handleApplyFilters, onAttributeChange } from '../../../../../utils';
import {
  ATTRIBUTE_FILTER_PROPTYPE,
  GENRE_PROPTYPE,
} from '../../../../../../../../constants/commonPropTypes';
import { Filter, Genre } from '../../../../../../../../constants/types';
import { SearchTextContext } from '../../../../../../../../contexts';
import styles from './AttributeFilterModal.module.scss';

export interface Props {
  isOpen: boolean;
  toggle: () => void;
  attributes: { genres: Genre[] };
  filters: Filter[];
  setFilters: (filters: Filter[]) => void;
}

const AttributeFilterModal = (
  { isOpen, attributes, toggle, filters, setFilters }: Props
) => {
  const { setSearchText, setShowSearchInput } = useContext(SearchTextContext);

  const {
    modalFilters,
    setModalFilters,
    handleCancelClick,
    handleApplyFiltersClick,
  } = useModalFilters(filters, setFilters, toggle);

  const {
    showDatePicker,
    startDatePickerFlow,
    getFiltersForDateSelection,
    completeDatePickerFlow,
    handleDatePickerCancel,
  } = useDatePickerFlow();


  const [filterByDate, setFilterByDate] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  const handleApplyFiltersWithClearSearchInput = () => {
    handleApplyFiltersClick();
    // Clear search text when applying attribute filters - this is the expected behavior
    setSearchText('');
    setShowSearchInput(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleFilterDateRangeClick = () => {
    const newFilters = [...filters].map(
      filter => ({
        ...filter,
        comparator: '=',
      } as Filter)
    );
    handleApplyFilters(newFilters);
    setFilters(newFilters);
    setFilterByDate(!filterByDate);
  };


  return (
    <Modal
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={handleCancelClick} type="link">
          Cancel
        </Button>,
        <Button key="clear" onClick={() => setModalFilters([])} type="link">
          Clear all filters
        </Button>,
        // TODO: finish enabling filtering by date range
        // <Button disabled={filterByDate} key="date" onClick={handleFilterDateRangeClick} type="default">
        //   Filter by date range
        // </Button>,
        <Button key="submit" onClick={handleApplyFiltersWithClearSearchInput} type="primary">
          Apply Filters
        </Button>,
      ]}
      onCancel={handleCancelClick}
      open={isOpen}
      title={(
        <div className={styles.modalHeader}>
          <span>Select Filters</span>
          {modalFilters.length > 0 && (
            <div>
              <Checkbox
                checked={showAdvancedOptions}
                className={styles.checkbox}
                onChange={() => setShowAdvancedOptions(!showAdvancedOptions)}
              >
                Show advanced options
              </Checkbox>
            </div>
          )}
        </div>
      )}
    >
      {modalFilters.map((filter, index) => (
        <AttributeFilterAndComparatorSelector
          filter={filter}
          filterByDate={filterByDate}
          index={index}
          key={index} // eslint-disable-line react/no-array-index-key
          modalFilters={modalFilters}
          setModalFilters={setModalFilters}
          showAdvancedOptions={showAdvancedOptions}
        />
      ))}
      <div style={{ marginTop: '10px' }}>
        <AttributePicker
          includeNilAndAny
          showUneditable
          showUnlistable
          attributes={attributes}
          onChange={(selection: (string | number)[]) => {
            const [genreSelected, sectionSelected, attributeTypeSelected, attributeSelected] = selection;

            if (attributeSelected === SELECT_DATE) {
              startDatePickerFlow(genreSelected as string, sectionSelected as string, attributeTypeSelected as string);
            } else {
              const updatedFilters = onAttributeChange(
                attributes,
                modalFilters,
                genreSelected as string,
                sectionSelected as string,
                attributeTypeSelected as string,
                attributeSelected as string
              );
              setModalFilters(updatedFilters);
            }
          }}
          placeholder="Choose an attribute to filter"
        />
      </div>

      <DatePickerModal
        isOpen={showDatePicker}
        onCancel={handleDatePickerCancel}
        onDateSelect={date => {
          const newFilters = getFiltersForDateSelection(date, attributes, modalFilters);
          if (newFilters) {
            setModalFilters(newFilters);
          }
          completeDatePickerFlow();
        }}
      />
    </Modal>
  );
};

AttributeFilterModal.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func.isRequired,
  attributes: PropTypes.shape({ genres: PropTypes.arrayOf(GENRE_PROPTYPE) }),
  filters: PropTypes.arrayOf(ATTRIBUTE_FILTER_PROPTYPE),
  setFilters: PropTypes.func.isRequired,
};

AttributeFilterModal.defaultProps = {
  isOpen: false,
  attributes: [],
  filters: [],
};

export default AttributeFilterModal;
